import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

interface User {
  id: number;
  username: string;
  fullName: string;
  email?: string;
  role: 'administrator' | 'coordinator' | 'donor';
  isRegistrationComplete?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    try {
      const token = localStorage.getItem('adminToken');
      const userData = localStorage.getItem('userData');
      if (token && userData) {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await fetch('https://backend.divisavi.org/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        try {
          localStorage.setItem('adminToken', data.token || 'authenticated');
          if (data.user) {
            localStorage.setItem('userData', JSON.stringify(data.user));
            setUser(data.user);
          }
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        setIsAuthenticated(true);
        return { success: true };
      } else {
        // Return error message from backend
        const errorMessage = data.message || 'Invalid username or password';
        console.error('Login failed:', errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.message || 'An error occurred. Please check your connection and try again.' 
      };
    }
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('userData');
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    isAuthenticated,
    user,
    login,
    logout,
    loading
  }), [isAuthenticated, user, login, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

